import { useState } from "react"
import { useLocation, useSearchParams } from "react-router"
import { Button } from "~/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog"
import { DocsUiLink as Link } from "~/docs-data/docs-link"
import {
  LEGACY_DOCS_PARAM,
  normalizeLegacyDocsPath,
  resolveLegacyDocsPath,
} from "~/docs-data/legacy-unacast-docs"

const GUIDES = "/docs/guides"

/**
 * Shown when a visitor arrives from an old docs.unacast.com URL
 * (`?from-docs=<old path>`, set by the legacy redirects in
 * `_console.docs.$.tsx` and the datasets country loader). Explains that the
 * Unacast documentation now lives here and whether this page is the
 * equivalent of the one they were looking for or just the closest starting
 * point (CAT-1204). Same pattern as the retired-blog-post dialog on
 * pine59.com.
 */
export function LegacyDocsDialog() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const [dismissed, setDismissed] = useState(false)

  const from = searchParams.get(LEGACY_DOCS_PARAM)
  if (dismissed || from === null) return null

  const oldPath = normalizeLegacyDocsPath(from)
  const target = resolveLegacyDocsPath(oldPath)
  // A `?from-docs` we don't recognise (hand-edited URL) still gets the
  // generic move notice, but no claim about equivalence.
  const match = target?.match ?? "nearest"
  const onGuides = /\/docs\/guides\/?$/.test(location.pathname)

  const close = () => {
    setDismissed(true)
    const next = new URLSearchParams(searchParams)
    next.delete(LEGACY_DOCS_PARAM)
    setSearchParams(next, { replace: true, preventScrollReset: true })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Looking for the Unacast documentation?
          </DialogTitle>
          <DialogDescription className="text-base text-foreground">
            You&rsquo;ve come to the right place.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            Pine59 is the new company continuing Unacast&rsquo;s Aggregated
            Location Insights business. The Unacast documentation has moved
            here, so links to docs.unacast.com now bring you to the Pine59 docs.
          </p>
          {match === "equivalent" ? (
            <p>
              This page is the Pine59 equivalent of the Unacast page you were
              looking for (
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {oldPath}
              </code>
              ).
            </p>
          ) : (
            <p>
              The Unacast page you were looking for (
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {oldPath}
              </code>
              ) has no direct equivalent in the Pine59 documentation. This is
              the closest starting point.
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-start">
          <Button onClick={close}>Continue</Button>
          {!onGuides && (
            <Link
              to={GUIDES}
              variant="ghost"
              onClick={() => setDismissed(true)}
            >
              Browse guides and examples
            </Link>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
